"use client";

import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight, ShieldCheck, Eye, Users, Lightbulb } from "lucide-react";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function About() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const pillars = [{
    title: "Data Integrity",
    description: "We employ rigorous validation protocols to ensure every data point meets institutional standards.",
    icon: ShieldCheck
  }, {
    title: "Transparency",
    description: "Our methodologies are open-source and peer-reviewed, providing full visibility into our calculations.",
    icon: Eye
  }, {
    title: "Collaboration",
    description: "We work alongside academic and industry experts to refine our economic modeling.",
    icon: Users
  }, {
    title: "Innovation",
    description: "Utilizing advanced analytics to capture real-time shifts in the Australian economic landscape.",
    icon: Lightbulb
  }];
  return <section data-section-id="234713" id="our-mission" className="bg-background text-foreground overflow-hidden py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={ref} className={`transform-gpu transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <div className="relative p-6 sm:p-10 md:p-16 bg-card border border-border shadow-sm">
            <div className="absolute top-0 left-0 right-0 h-[1px] bg-primary/20 z-20" />
            <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-primary/20 z-20" />
            <div className="absolute left-0 top-0 bottom-0 w-[1px] bg-primary/20 z-20" />
            <div className="absolute right-0 top-0 bottom-0 w-[1px] bg-primary/20 z-20" />

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center relative z-10">
              
              <div className="lg:col-span-7 flex flex-col md:flex-row gap-6 md:gap-10">
                <div className="hidden md:flex items-center justify-center w-16 shrink-0 relative">
                  <h2 className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 -rotate-90 text-7xl lg:text-8xl font-bold text-primary/5 tracking-tighter whitespace-nowrap select-none font-default">
                    MISSION
                  </h2>
                </div>

                <div className="font-default text-[1.125rem] leading-[1.8] text-foreground/90">
                  <h2 className="md:hidden text-4xl font-bold text-primary mb-6 tracking-tight font-default">
                    OUR MISSION
                  </h2>
                  <p className="mb-8 text-xl md:text-2xl font-medium text-primary leading-relaxed">
                    To provide clear, credible, and accessible economic insights that help Australians make informed decisions about their households, businesses, and communities.
                  </p>
                  <p className="mb-10">
                    A nation empowered by transparent, data‑driven indicators that reflect real conditions and support better decision‑making at every level, from government to everyday Australians.
                  </p>
                  
                  <Button variant="outline" size="lg" className="rounded-sm border border-primary text-primary hover:bg-primary hover:text-primary-foreground font-default uppercase tracking-wider group transition-all duration-200" asChild>
                    <Link to="/MethodologyPage">
                      Review Methodology
                      <ArrowRight className="ml-2 size-5 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </Button>

                  <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 gap-8">
                    {pillars.map((pillar, i) => <div key={pillar.title} data-index={i} className="flex flex-col gap-3">
                        <div className="flex items-center gap-3 text-accent-dark">
                          <pillar.icon className="size-5" />
                          <h3 className="font-bold text-sm uppercase tracking-widest text-primary">{pillar.title}</h3>
                        </div>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {pillar.description}
                        </p>
                      </div>)}
                  </div>
                </div>
              </div>

              <div className="lg:col-span-5 relative">
                <div className="relative aspect-[3/4] w-full max-w-md mx-auto bg-muted overflow-hidden border border-border">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1664816/img/institutional_architecture.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1664816/img/institutional_architecture.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1664816/img/institutional_architecture.png-wvc-sizes" alt="Modern Australian architectural detail" className="object-cover w-full h-full grayscale opacity-90 contrast-125" loading="lazy" decoding="async" />
                  
                  <div className="absolute inset-0 pointer-events-none bg-[linear-gradient(transparent_98%,rgba(0,0,0,0.05)_2%)] bg-[length:100%_40px] z-10" />
                  
                  <div className="absolute inset-0 bg-primary/5 pointer-events-none z-20" />
                  
                  <div className="absolute top-4 left-4 w-8 h-8 border-t border-l border-primary/40 z-30" />
                  <div className="absolute bottom-4 right-4 w-8 h-8 border-b border-r border-primary/40 z-30" />
                </div>
              </div>

            </div>

            <div className="mt-16 pt-8 border-t border-border/50 relative z-10">
              <p className="text-sm md:text-base text-muted-foreground italic max-w-3xl">
                AIB operates independently, without commercial bias or political influence, ensuring that every index reflects genuine economic conditions rather than opinion or agenda.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}